import { useId, useRef, useState, type ReactNode } from 'react';

interface TooltipProps {
  label: string;
  children: ReactNode;
}

/**
 * Accessible tooltip that works on hover AND keyboard focus (never hover-only).
 * The trigger's own accessible name is left intact; the hint is exposed via
 * aria-describedby only while visible.
 */
export function Tooltip({ label, children }: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLSpanElement>(null);

  return (
    <span
      ref={wrapper}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
      aria-describedby={open ? id : undefined}
    >
      {children}
      {open ? (
        <span
          id={id}
          role="tooltip"
          className="pointer-events-none absolute bottom-[calc(100%+0.5rem)] left-1/2 z-40 w-max max-w-64 -translate-x-1/2 rounded-md bg-navy px-2.5 py-1.5 text-xs font-medium text-white shadow-raised"
        >
          {label}
        </span>
      ) : null}
    </span>
  );
}
